import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import {
  useGetUserInfoQuery,
  useUpdateUserProfileMutation,
} from "../../features/user/userApiSlice";
import { useUploadMultipleMutation } from "../../features/upload/uploadApiSlice";
import { getAsset } from "../../helpers/helper";
import dummyUser from "../../assets/images/dummy/dummy-user.png";
import "./profilesetting.css";

/**
 * Resolves current profile image URL from user entity
 */
const resolveUserPhoto = (userData) => {
  if (!userData) return null;
  const candidate =
    (Array.isArray(userData.imageUrl) && userData.imageUrl.length > 0
      ? userData.imageUrl
      : null) ||
    userData.imageUrl ||
    userData.profileImage ||
    userData.profilePic ||
    userData.avatar;

  if (!candidate) return null;
  if (Array.isArray(candidate)) {
    const first = candidate.flat(Infinity).find((item) => {
      if (!item) return false;
      if (typeof item === "string") return item.trim() !== "";
      if (typeof item === "object") return Boolean(item.url || item.path || item.src);
      return false;
    });
    return first ? getAsset(first) : null;
  }
  if (typeof candidate === "string" && candidate.trim() !== "") {
    return getAsset(candidate);
  }
  return null;
};

const Profilesetting = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const reduxUser = useSelector((state) => state.user?.name);
  const userId = reduxUser?._id;

  // RTK Query: fetch user information
  const { data: userInfoRes, isLoading: isFetchingUser } = useGetUserInfoQuery(
    userId,
    { skip: !userId }
  );

  const [updateUserProfile, { isLoading: isUpdatingProfile }] =
    useUpdateUserProfileMutation();

  const [uploadMultiple, { isLoading: isUploadingImage }] =
    useUploadMultipleMutation();

  const user = userInfoRes?.data?.User || reduxUser;

  // Local Form State
  const [formData, setFormData] = useState({
    fullname: "",
    phonenumber: "",
    country: "",
    state: "",
    city: "",
    status: true,
  });

  const [educationInputs, setEducationInputs] = useState([""]);
  const [previewImage, setPreviewImage] = useState(null);
  const [imgError, setImgError] = useState(false);

  // Synchronize form with latest user profile
  useEffect(() => {
    if (user) {
      setFormData({
        fullname: user?.fullname || "",
        phonenumber: user?.phonenumber || "",
        country: user?.address?.country || user?.MCountry || "",
        state: user?.address?.state || user?.MProvince || "",
        city: user?.address?.city || user?.MCity || user?.selectedCity || "",
        status: user?.status !== false,
      });

      if (Array.isArray(user?.education) && user.education.length > 0) {
        setEducationInputs(user.education);
      } else {
        setEducationInputs([""]);
      }
      setImgError(false);
      setPreviewImage(null);
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Profile image upload handler
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!userId) {
      toast.error("Please login to upload a profile photo.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB.");
      return;
    }

    // Instant local preview
    const localUrl = URL.createObjectURL(file);
    setPreviewImage(localUrl);
    setImgError(false);

    // Upload file to server
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("images", file);

      const uploadRes = await uploadMultiple(uploadFormData).unwrap();

      const extractImagePath = (f) => {
        if (!f) return null;
        if (typeof f === "string") {
          if (f.includes("undefined")) return null;
          return f.startsWith("/") ? f : "/images/" + f;
        }
        const fname =
          f?.filename || f?.name || (f?.url ? f.url.split("/").pop() : null);
        if (!fname || fname === "undefined") return null;
        return fname.startsWith("/") ? fname : "/images/" + fname;
      };

      const rawFiles =
        uploadRes?.file || uploadRes?.files || uploadRes?.urls || [];
      const imagePaths = (
        Array.isArray(rawFiles) ? rawFiles : [rawFiles]
      )
        .map(extractImagePath)
        .filter(Boolean);

      if (imagePaths.length > 0) {
        const response = await updateUserProfile({
          id: userId,
          data: { imageUrl: imagePaths },
        }).unwrap();

        if (response?.success || response?.data?.User) {
          toast.success("Profile photo updated successfully!");
          window.dispatchEvent(
            new CustomEvent("image-upload-success", {
              detail: {
                userId,
                imageUrl: imagePaths[0],
                timestamp: new Date().toISOString(),
              },
            })
          );
        }
      } else {
        toast.error("Failed to parse uploaded image. Please try again.");
      }
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Failed to upload profile photo."
      );
      setPreviewImage(null);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Remove photo handler
  const handleRemovePhoto = async () => {
    if (!userId) return;

    try {
      await updateUserProfile({
        id: userId,
        data: { imageUrl: [] },
      }).unwrap();

      setPreviewImage(null);
      setImgError(false);
      toast.success("Profile photo removed.");
      window.dispatchEvent(
        new CustomEvent("image-upload-success", {
          detail: {
            userId,
            imageUrl: null,
            timestamp: new Date().toISOString(),
          },
        })
      );
    } catch (err) {
      toast.error("Failed to remove profile photo.");
    }
  };

  // Education dynamic list handlers
  const handleEducationChange = (index, value) => {
    const updated = [...educationInputs];
    updated[index] = value;
    setEducationInputs(updated);
  };

  const handleAddEducation = () => {
    setEducationInputs((prev) => [...prev, ""]);
  };

  const handleRemoveEducation = (index) => {
    if (educationInputs.length <= 1) {
      setEducationInputs([""]);
      return;
    }
    const updated = educationInputs.filter((_, idx) => idx !== index);
    setEducationInputs(updated);
  };

  // Reset form to latest loaded user state
  const handleReset = () => {
    if (user) {
      setFormData({
        fullname: user?.fullname || "",
        phonenumber: user?.phonenumber || "",
        country: user?.address?.country || user?.MCountry || "",
        state: user?.address?.state || user?.MProvince || "",
        city: user?.address?.city || user?.MCity || user?.selectedCity || "",
        status: user?.status !== false,
      });

      if (Array.isArray(user?.education) && user.education.length > 0) {
        setEducationInputs(user.education);
      } else {
        setEducationInputs([""]);
      }
      setPreviewImage(null);
      setImgError(false);
    }
  };

  // Save changes to profile details
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!userId) {
      toast.error("Please login to update your profile settings.");
      return;
    }

    try {
      const filteredEducation = educationInputs
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean);

      const payload = {
        fullname: formData.fullname.trim(),
        phonenumber: formData.phonenumber.trim(),
        address: {
          country: formData.country.trim(),
          state: formData.state.trim(),
          city: formData.city.trim(),
        },
        status: formData.status,
        education: filteredEducation,
      };

      await updateUserProfile({ id: userId, data: payload }).unwrap();
      toast.success("Profile settings updated successfully!");
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.message ||
          "Failed to update profile settings. Please try again."
      );
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/SellingSide");
    }
  };

  // Compute avatar source
  const currentPhoto = resolveUserPhoto(user);
  const avatarDisplay =
    previewImage || (!imgError && currentPhoto ? currentPhoto : dummyUser);
  const hasUploadedPhoto = Boolean(previewImage || currentPhoto);

  return (
    <div className="profile-settings-page">
      <Container className="profile-settings-container">
        <div className="profile-settings-card">
          {/* Header */}
          <div className="profile-settings-header">
            <div className="profile-settings-header-left">
              <button
                type="button"
                className="profile-settings-back-btn"
                onClick={handleBack}
                title="Go back"
                aria-label="Go back"
              >
                <ArrowBackIcon style={{ fontSize: 20 }} />
              </button>
              <div>
                <h1 className="profile-settings-title">Profile settings</h1>
                <p className="profile-settings-subtitle">
                  Manage your personal details, photo, location preferences, and account status.
                </p>
              </div>
            </div>

            <div
              className={`profile-settings-status-pill ${
                formData.status ? "active" : "inactive"
              }`}
            >
              <span className="profile-settings-status-dot" />
              <span>{formData.status ? "Active account" : "Inactive account"}</span>
            </div>
          </div>

          {/* Profile Photo Upload Section */}
          <div className="profile-settings-photo-card">
            <div className="profile-settings-photo-avatar-wrapper">
              <img
                src={avatarDisplay}
                alt="Profile avatar"
                className="profile-settings-photo-img"
                onError={() => setImgError(true)}
              />
              {isUploadingImage && (
                <div className="profile-settings-photo-loading">Uploading...</div>
              )}
            </div>

            <div className="profile-settings-photo-actions">
              <div className="profile-settings-photo-btn-group">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  className="profile-settings-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                >
                  <PhotoCameraOutlinedIcon style={{ fontSize: 17 }} />
                  <span>{hasUploadedPhoto ? "Change photo" : "Upload photo"}</span>
                </button>

                {hasUploadedPhoto && (
                  <button
                    type="button"
                    className="profile-settings-remove-photo-btn"
                    onClick={handleRemovePhoto}
                    disabled={isUploadingImage}
                  >
                    <DeleteOutlineIcon style={{ fontSize: 17 }} />
                    <span>Remove</span>
                  </button>
                )}
              </div>
              <p className="profile-settings-photo-hint">
                Accepted formats: JPG, PNG, WEBP. Max file size: 5MB.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Section 1: Personal Details */}
            <div className="profile-settings-section">
              <div className="profile-settings-section-title">
                <PersonOutlineIcon className="profile-settings-section-icon" />
                <span>Personal information</span>
              </div>

              <Row>
                <Col md={6} xs={12}>
                  <div className="profile-settings-form-group">
                    <label className="profile-settings-label" htmlFor="settings-fullname">
                      Full name
                      <span className="profile-settings-label-note">
                        (Note: you can change your name after 6 months only)
                      </span>
                    </label>
                    <input
                      id="settings-fullname"
                      type="text"
                      className="profile-settings-input"
                      placeholder="Enter your full name"
                      value={formData.fullname}
                      onChange={(e) => handleInputChange("fullname", e.target.value)}
                    />
                  </div>
                </Col>

                <Col md={6} xs={12}>
                  <div className="profile-settings-form-group">
                    <label className="profile-settings-label" htmlFor="settings-phone">
                      Phone number <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      id="settings-phone"
                      type="text"
                      className="profile-settings-input"
                      placeholder="Enter phone number"
                      value={formData.phonenumber}
                      onChange={(e) => handleInputChange("phonenumber", e.target.value)}
                      required
                    />
                  </div>
                </Col>
              </Row>
            </div>

            {/* Section 2: Location Details */}
            <div className="profile-settings-section">
              <div className="profile-settings-section-title">
                <LocationOnOutlinedIcon className="profile-settings-section-icon" />
                <span>Location information</span>
              </div>

              <Row>
                <Col md={4} xs={12}>
                  <div className="profile-settings-form-group">
                    <label className="profile-settings-label" htmlFor="settings-country">
                      Country
                    </label>
                    <input
                      id="settings-country"
                      type="text"
                      className="profile-settings-input"
                      placeholder="e.g. United States"
                      value={formData.country}
                      onChange={(e) => handleInputChange("country", e.target.value)}
                    />
                  </div>
                </Col>

                <Col md={4} xs={12}>
                  <div className="profile-settings-form-group">
                    <label className="profile-settings-label" htmlFor="settings-state">
                      State / province
                    </label>
                    <input
                      id="settings-state"
                      type="text"
                      className="profile-settings-input"
                      placeholder="e.g. California"
                      value={formData.state}
                      onChange={(e) => handleInputChange("state", e.target.value)}
                    />
                  </div>
                </Col>

                <Col md={4} xs={12}>
                  <div className="profile-settings-form-group">
                    <label className="profile-settings-label" htmlFor="settings-city">
                      City
                    </label>
                    <input
                      id="settings-city"
                      type="text"
                      className="profile-settings-input"
                      placeholder="e.g. Los Angeles"
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            {/* Section 3: Status Toggle */}
            <div className="profile-settings-section">
              <div className="profile-settings-section-title">
                <CheckCircleOutlineIcon className="profile-settings-section-icon" />
                <span>Current status</span>
              </div>

              <div className="profile-settings-radio-group">
                <div
                  className={`profile-settings-radio-card ${
                    formData.status ? "selected" : ""
                  }`}
                  onClick={() => handleInputChange("status", true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleInputChange("status", true);
                    }
                  }}
                >
                  <div className="profile-settings-radio-circle">
                    {formData.status && <div className="profile-settings-radio-dot" />}
                  </div>
                  <span>Active</span>
                </div>

                <div
                  className={`profile-settings-radio-card ${
                    !formData.status ? "selected" : ""
                  }`}
                  onClick={() => handleInputChange("status", false)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleInputChange("status", false);
                    }
                  }}
                >
                  <div className="profile-settings-radio-circle">
                    {!formData.status && <div className="profile-settings-radio-dot" />}
                  </div>
                  <span>Inactive</span>
                </div>
              </div>
            </div>

            {/* Section 4: Education Credentials */}
            <div className="profile-settings-section">
              <div className="profile-settings-section-title">
                <SchoolOutlinedIcon className="profile-settings-section-icon" />
                <span>Education & qualifications</span>
              </div>

              <div>
                {educationInputs.map((edu, index) => (
                  <div key={index} className="profile-settings-education-row">
                    <div className="profile-settings-education-input">
                      <input
                        type="text"
                        className="profile-settings-input"
                        placeholder="e.g. Bachelor in Computer Science - Oxford University"
                        value={edu}
                        onChange={(e) => handleEducationChange(index, e.target.value)}
                      />
                    </div>

                    {educationInputs.length > 1 && (
                      <button
                        type="button"
                        className="profile-settings-icon-btn remove"
                        onClick={() => handleRemoveEducation(index)}
                        title="Remove qualification"
                        aria-label="Remove qualification"
                      >
                        <DeleteOutlineIcon style={{ fontSize: 18 }} />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  className="profile-settings-add-btn"
                  onClick={handleAddEducation}
                >
                  <AddCircleOutlineIcon style={{ fontSize: 17 }} />
                  <span>Add another qualification</span>
                </button>
              </div>
            </div>

            {/* Footer Action Buttons */}
            <div className="profile-settings-actions">
              <button
                type="button"
                className="profile-settings-btn-secondary"
                onClick={handleReset}
                disabled={isUpdatingProfile || isUploadingImage}
              >
                Reset
              </button>
              <button
                type="submit"
                className="profile-settings-btn-primary"
                disabled={isUpdatingProfile || isFetchingUser || isUploadingImage}
              >
                {isUpdatingProfile ? "Updating..." : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      </Container>
    </div>
  );
};

export default Profilesetting;
